// Spread radial permanente para markers superpuestos.
//
// El backend C5 emite incidentes 911 distintos en la MISMA coordenada
// (follow-ups, "incidente repetido", llamadas múltiples sobre el mismo
// cruce). Sin spread, MapLibre los apila por iconSortKey y el operador
// solo ve uno; al hacer click, pickClickedFeature puede devolver cualquiera.
//
// Estrategia: cuando detectamos N>=2 en una coord (tolerancia ~1m),
// desplazamos cada feature con icon-offset data-driven en píxeles. La
// coord original se mantiene en feature.geometry para que el click-handler
// siga apuntando al lat/lon real.
//
// El halo de circle-layer NO soporta translate por feature, así que los
// halos individuales quedan apilados en el centro y se perciben como un
// halo combinado — sirve de indicador visual de "aquí hay varios".

import { LABEL_TEXT_SIZE_PX } from '../utils/map-constants.js'

// Tolerancia para considerar dos coords como "iguales". 1e-5 grados ≈ 1.1m
// en el ecuador. Suficiente para capturar duplicados exactos del backend
// sin mezclar incidentes en domicilios vecinos (~10m de separación).
var COORD_TOLERANCE_DEG = 1e-5

// Radio del círculo de spread en píxeles, por tamaño de grupo. Tabla
// discreta calibrada visualmente por el operador. Para N grandes el
// radio sube progresivamente para que la distancia entre vecinos siga
// dejando aire (~45-55 px). Producción tiene grupos hasta de N=11.
//
// Distancia entre vecinos = 2·r·sin(π/N):
//   N=2 → r=28  → 56 px
//   N=3 → r=32  → ≈55 px
//   N=4 → r=38  → ≈54 px
//   N=5 → r=46  → ≈54 px
//   N=6-7 → r=56  → 56 / ≈49 px
//   N=8-10 → r=70  → ≈54 / 41 px
//   N=11+ → r=90  → ≈51 px (N=11)
function pickRadiusPx(N) {
  if (N <= 2) return 28
  if (N === 3) return 32
  if (N === 4) return 38
  if (N === 5) return 46
  if (N <= 7) return 56
  if (N <= 10) return 70
  return 90
}

// Solo aplicamos spread a este conjunto de entityTypes. Devices/cameras/etc
// rara vez comparten coord exacta y no necesitan el patrón visual del
// dispatch CAD.
var SPREAD_ENTITY_TYPES = { incident: 1 }

function _coordKey(coords) {
  // Bucketizar por la tolerancia. Round a 5 decimales captura cualquier
  // par de coords que difieran en menos de ~1m.
  var lng = Math.round(coords[0] / COORD_TOLERANCE_DEG)
  var lat = Math.round(coords[1] / COORD_TOLERANCE_DEG)
  return lng + ',' + lat
}

/**
 * Calcula los offsets de spread para un conjunto de features.
 * @param {Iterable<Object>} features - GeoJSON features (cada uno con
 *   properties.monitorId, properties.entityType y geometry.coordinates)
 * @returns {Map<string, {iconOffsetPx: [number, number], spreadActive: boolean, spreadGroupSize: number}>}
 *   Mapa monitorId → propiedades de spread. Los monitorIds que no pertenezcan
 *   a un grupo con N>=2 NO aparecen en el resultado (interpretarse como
 *   "sin spread", iconOffsetPx [0,0]).
 */
export function computeSpreads(features) {
  var groups = new Map()
  // Soporta arrays, Map.values() y cualquier iterable (for..of funciona).
  for (var f of features) {
    if (!f || !f.properties || !f.geometry) continue
    if (!SPREAD_ENTITY_TYPES[f.properties.entityType]) continue
    var key = _coordKey(f.geometry.coordinates)
    var bucket = groups.get(key)
    if (!bucket) { bucket = []; groups.set(key, bucket) }
    bucket.push(f)
  }

  var result = new Map()
  groups.forEach(function(group) {
    var N = group.length
    if (N < 2) return
    // Orden estable por monitorId para que el ángulo asignado a cada
    // feature no cambie entre rebuilds del source (de lo contrario los
    // markers "saltarían" de posición visual a cada flush).
    group.sort(function(a, b) {
      return String(a.properties.monitorId).localeCompare(String(b.properties.monitorId))
    })
    var radius = pickRadiusPx(N)
    var angleStep = (2 * Math.PI) / N
    // Arrancar el primero en 12 en punto (top), ir en sentido horario.
    // Convención de pantalla: y positivo hacia abajo en MapLibre icon-offset,
    // por eso +sin (no -sin) para que el segundo quede a la derecha-abajo.
    var startAngle = -Math.PI / 2
    for (var j = 0; j < N; j++) {
      var angle = startAngle + j * angleStep
      var dx = Math.cos(angle) * radius
      var dy = Math.sin(angle) * radius
      result.set(group[j].properties.monitorId, {
        iconOffsetPx: [dx, dy],
        spreadActive: true,
        spreadGroupSize: N,
      })
    }
  })
  return result
}

/**
 * Aplica los offsets calculados a las properties de las features in-place.
 * Las features que no estén en el mapa de spreads se resetean a iconOffsetPx
 * [0,0] / spreadActive false (importante porque el spread se recalcula en
 * cada flush — un grupo que se disolvió debe volver a la coord real).
 * @param {Iterable<Object>} features
 * @param {Map<string, Object>} spreads - resultado de computeSpreads()
 */
export function applySpreads(features, spreads) {
  var apply = function(feat) {
    if (!feat || !feat.properties) return
    if (!SPREAD_ENTITY_TYPES[feat.properties.entityType]) {
      // Para entity types que no participan del spread, NO tocar las props
      // (mantenemos el feature liviano sin keys spurias).
      return
    }
    var entry = spreads.get(feat.properties.monitorId)
    if (entry) {
      feat.properties.iconOffsetPx = entry.iconOffsetPx
      feat.properties.spreadActive = true
      feat.properties.spreadGroupSize = entry.spreadGroupSize
      // Conversión px → em para que la label siga al icono spreadeado.
      // text-size=LABEL_TEXT_SIZE_PX → 1em = LABEL_TEXT_SIZE_PX px.
      // Baseline 1.8em es la posición default de la label debajo del marker.
      feat.properties.textOffsetEm = [
        entry.iconOffsetPx[0] / LABEL_TEXT_SIZE_PX,
        entry.iconOffsetPx[1] / LABEL_TEXT_SIZE_PX + 1.8,
      ]
    } else {
      // Reset a default cuando este feature dejó de estar en un grupo.
      feat.properties.iconOffsetPx = [0, 0]
      feat.properties.spreadActive = false
      feat.properties.spreadGroupSize = 1
      feat.properties.textOffsetEm = [0, 1.8]
    }
  }
  for (var feat of features) apply(feat)
}
